<script setup lang="ts">
/**
 * Section 105 — Form of Transfer of Securities (Companies Act 2016).
 * 원본 docx 법정 양식을 그대로 재현: 좌측 항목(1~6) + 우측 서명란(양도인·증인2 / 양수인·증인2) 병렬 그리드.
 * 항목 6은 Person / Body Corporate 두 칼럼 동시 표시(Race / Type 체크박스). 양도 1건 = 1부(index 바인딩).
 */
import { computed, inject } from 'vue';
import { TRANSFER_DOC_CTX } from './transferContext';
import TransferDocVarField from './TransferDocVarField.vue';

const props = defineProps<{ index: number }>();
const ctx = inject(TRANSFER_DOC_CTX)!;
const k = (field: string) => `s105_${props.index}_${field}`;
const race = computed(() => ctx.raceOf(props.index));
const bodyType = computed(() => ctx.bodyTypeOf(props.index));
</script>

<template>
  <div class="s105doc">
    <!-- 좌상단 Company Registration No. 박스 -->
    <div class="s105-regbox">
      <div class="s105-reglbl">Company Registration No.</div>
      <div class="s105-regval">[<TransferDocVarField field-key="company_registration_no" /> (<TransferDocVarField field-key="old_registration_no" />)]</div>
    </div>

    <!-- 중앙 제목 -->
    <div class="s105-title">
      <div>COMPANIES ACT 2016</div>
      <div><b>Section 105</b></div>
      <div class="s105-formtitle">FORM OF TRANSFER OF SECURITIES</div>
    </div>

    <!-- 메인 그리드: 좌(항목) | 우(서명) -->
    <table class="s105-outer">
      <tbody>
        <tr>
          <!-- ── 좌측: 항목 1~6 ── -->
          <td class="s105-left">
            <table class="s105-items">
              <tbody>
                <tr>
                  <td class="it-lbl">1 . Name of Company: <span class="sub">(In Block Letters)</span></td>
                  <td class="it-val">
                    <TransferDocVarField field-key="company_name" /><br />
                    [COMPANY REGISTRATION NO. <TransferDocVarField field-key="company_registration_no" />]
                  </td>
                </tr>
                <tr>
                  <td class="it-lbl"><b>2. Number of Units</b> <span class="sub">(In Figures &amp; Words)</span><br /><TransferDocVarField :field-key="k('number_of_units')" /></td>
                  <td class="it-val it-split">
                    <div class="sp"><div class="sp-h">Description of Securities</div><TransferDocVarField :field-key="k('securities_description')" /></div>
                    <div class="sp"><div class="sp-h">Certificate Number(s), if any</div><TransferDocVarField :field-key="k('certificate_no')" /></div>
                  </td>
                </tr>
                <tr>
                  <td class="it-lbl">3. Name of Transferor(s) : <span class="sub">(In Block Letters) (Hereinafter Called "Transferor")</span></td>
                  <td class="it-val"><TransferDocVarField :field-key="k('transferor_details')" /></td>
                </tr>
                <tr>
                  <td class="it-lbl">4. Identity Card/Passport/Company No:</td>
                  <td class="it-val"><TransferDocVarField :field-key="k('transferor_id')" /></td>
                </tr>
                <tr>
                  <td class="it-lbl">5. Name &amp; Address of Transferee(s) : <span class="sub">(In Block Letters) (Hereinafter Called "Transferee")</span></td>
                  <td class="it-val"><TransferDocVarField :field-key="k('transferee_details')" /></td>
                </tr>
                <tr>
                  <td class="it-full" colspan="2">6. Particulars of Transferee <span class="sub">(<i>See</i> Columns below) :</span></td>
                </tr>
                <tr>
                  <td class="it-part" colspan="2">
                    <div class="part-grid">
                      <!-- Person -->
                      <div class="part-col">
                        <div class="part-h">(If Transferee is a Person) <span class="sub">§ Identity Card/ Passport No.</span></div>
                        <div class="part-h">Nationality</div>
                        <TransferDocVarField :field-key="k('transferee_person')" />
                        <div class="part-sub">Race (If Malaysian) :</div>
                        <div class="cbx" @click="ctx.setRace(index, 'malay')"><span class="box">{{ race === 'malay' ? '✓' : '' }}</span>Malay</div>
                        <div class="cbx" @click="ctx.setRace(index, 'chinese')"><span class="box">{{ race === 'chinese' ? '✓' : '' }}</span>Chinese</div>
                        <div class="cbx" @click="ctx.setRace(index, 'indian')"><span class="box">{{ race === 'indian' ? '✓' : '' }}</span>Indian</div>
                        <div class="cbx" @click="ctx.setRace(index, 'others')"><span class="box">{{ race === 'others' ? '✓' : '' }}</span>Others (Specify)</div>
                      </div>
                      <!-- Body Corporate -->
                      <div class="part-col">
                        <div class="part-h">(If Transferee is a Body Corporate) <span class="sub">Company No. /Registration No.</span></div>
                        <div class="part-h">Place of Incorporation</div>
                        <TransferDocVarField :field-key="k('transferee_corporate')" />
                        <div class="part-sub">Type of Body Corporate:</div>
                        <div class="cbx" @click="ctx.setBodyType(index, 'gov')"><span class="box">{{ bodyType === 'gov' ? '✓' : '' }}</span><span>Government Agencies/ Institution<br />Statutory Bodies</span></div>
                        <div class="cbx" @click="ctx.setBodyType(index, 'mn')"><span class="box">{{ bodyType === 'mn' ? '✓' : '' }}</span><span>Controlled by Malaysians<br />(Malays/Natives)</span></div>
                        <div class="cbx" @click="ctx.setBodyType(index, 'mnn')"><span class="box">{{ bodyType === 'mnn' ? '✓' : '' }}</span><span>Controlled by Malaysians<br />(Non-Malays/Non-Natives)</span></div>
                        <div class="cbx" @click="ctx.setBodyType(index, 'nm')"><span class="box">{{ bodyType === 'nm' ? '✓' : '' }}</span><span>Controlled by Non-Malaysians</span></div>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </td>

          <!-- ── 우측: 서명란 ── -->
          <td class="s105-right">
            <table class="s105-sigs">
              <tbody>
                <tr><td class="sig-cell">
                  <div class="sig-hd">Signature of Transferor (s)</div>
                  <div class="sig-line">--------------------------------------------------</div>
                  <div class="sig-name"><TransferDocVarField :field-key="k('transferor_name')" /></div>
                </td></tr>
                <tr><td class="sig-cell"><div class="wit">
                  <div>Signed, sealed and delivered in the presence of:</div>
                  <div class="wit-dots">.................................................</div>
                  <div>Signature</div>
                  <div>Name: <TransferDocVarField :field-key="k('witness_1_name')" /></div>
                  <div>Address: <TransferDocVarField :field-key="k('witness_1_address')" /></div>
                  <div>Occupation: <TransferDocVarField :field-key="k('witness_1_occupation')" /></div>
                </div></td></tr>
                <tr><td class="sig-cell"><div class="wit">
                  <div>Signed, sealed and delivered in the presence of:</div>
                  <div class="wit-dots">.................................................</div>
                  <div>Signature</div>
                  <div>Name: <TransferDocVarField :field-key="k('witness_2_name')" /></div>
                  <div>Address: <TransferDocVarField :field-key="k('witness_2_address')" /></div>
                  <div>Occupation: <TransferDocVarField :field-key="k('witness_2_occupation')" /></div>
                </div></td></tr>
                <tr><td class="sig-cell">
                  <div class="sig-hd">Signature of Transferee (s)</div>
                  <div class="sig-line">--------------------------------------------------</div>
                  <div class="sig-name"><TransferDocVarField :field-key="k('transferee_name')" /></div>
                </td></tr>
                <tr><td class="sig-cell"><div class="wit">
                  <div>Signed, sealed and delivered in the presence of:</div>
                  <div class="wit-dots">.................................................</div>
                  <div>Signature</div>
                  <div>Name: <TransferDocVarField :field-key="k('witness_3_name')" /></div>
                  <div>Address: <TransferDocVarField :field-key="k('witness_3_address')" /></div>
                  <div>Occupation: <TransferDocVarField :field-key="k('witness_3_occupation')" /></div>
                </div></td></tr>
                <tr><td class="sig-cell"><div class="wit">
                  <div>Signed, sealed and delivered in the presence of:</div>
                  <div class="wit-dots">.................................................</div>
                  <div>Signature</div>
                  <div>Name: <TransferDocVarField :field-key="k('witness_4_name')" /></div>
                  <div>Address: <TransferDocVarField :field-key="k('witness_4_address')" /></div>
                  <div>Occupation: <TransferDocVarField :field-key="k('witness_4_occupation')" /></div>
                </div></td></tr>
              </tbody>
            </table>
          </td>
        </tr>

        <!-- ── 하단 전체폭: 안내문 + 항목 7·8 ── -->
        <tr>
          <td class="s105-note" colspan="2">
            The consideration sum set forth in a transfer may differ from that which the first seller will receive owing to sub-sales by the
            original buyer; the Stamp Act 1949 requires that in such cases the consideration money paid by the sub-purchaser shall be the one
            inserted in the form, as regulating <i>ad valorem</i> duty (Stamp Act 1949). Please refer to item 32 <i>(b)</i>, First Schedule of the
            Stamp Act 1949 for the stamp duty chargeable on sale of any stock, shares or marketable securities. If the Transferor or Transferee is a
            body corporate, the execution of this instrument must be done in accordance with the provision of the constitution of its incorporation.
          </td>
        </tr>
        <tr><td class="s105-line" colspan="2">┼ *I am/* We are nominee (s) of a person resident in ... (country)</td></tr>
        <tr><td class="s105-line" colspan="2">
          <b>7.</b> In consideration of the sum herein mentioned, *I/* We, the Transferor, hereby transfer to the Transferee the securities referred
          to in paragraphs 1 and 2 hereof and *I/* We, the Transferee, hereby accept the transfer of the said securities.
        </td></tr>
        <tr><td class="s105-line" colspan="2">Consideration Sum (in words) : <TransferDocVarField :field-key="k('consideration_words')" /></td></tr>
        <tr><td class="s105-line" colspan="2">Consideration Sum (In Malaysian Ringgit) : RM<TransferDocVarField :field-key="k('consideration_figures')" /></td></tr>
        <tr><td class="s105-line" colspan="2"><b>8. Dated this</b> <TransferDocVarField field-key="transfer_date" /></td></tr>
      </tbody>
    </table>

    <!-- 각주 -->
    <div class="s105-foot">
      <div>A husband must not witness the signature of his wife or vice versa.</div>
      <div>* Strike out whichever is inapplicable.</div>
      <div>┼ Delete if inapplicable.</div>
      <div><i>§ Also state colour &amp; type / Also state country.</i></div>
    </div>
  </div>
</template>

<style scoped>
.s105doc { font-size: 10.5px; line-height: 1.4; color: #111; }
.s105-regbox { width: 260px; margin-bottom: 10px; }
.s105-reglbl { font-weight: 800; font-size: 12px; }
.s105-regval { border: 1px solid #000; padding: 3px 6px; margin-top: 2px; }
.s105-title { text-align: center; margin: 6px 0 12px; }
.s105-title .s105-formtitle { font-weight: 800; margin-top: 6px; }

.s105-outer { width: 100%; border-collapse: collapse; table-layout: fixed; }
.s105-outer > tbody > tr > td { border: 1px solid #000; vertical-align: top; padding: 0; }
.s105-left { width: 62%; }
.s105-right { width: 38%; }

/* 좌측 항목 표 */
.s105-items { width: 100%; border-collapse: collapse; }
.s105-items td { border: 1px solid #000; padding: 4px 6px; vertical-align: top; }
.s105-items .it-lbl { width: 46%; }
.s105-items .it-lbl .sub, .s105-items .part-h .sub, .s105-title + * .sub { font-weight: 400; }
.sub { color: #222; font-size: .95em; }
.it-val { min-height: 22px; }
.it-split { padding: 0; }
.it-split .sp { padding: 4px 6px; }
.it-split .sp + .sp { border-top: 1px solid #000; }
.it-split .sp-h { font-size: .95em; margin-bottom: 2px; }
.it-full { font-weight: 400; }
.it-part { padding: 0; }
.part-grid { display: grid; grid-template-columns: 1fr 1fr; }
.part-col { padding: 5px 7px; }
.part-col + .part-col { border-left: 1px solid #000; }
.part-h { margin-bottom: 3px; }
.part-sub { font-weight: 700; margin: 6px 0 4px; }
.cbx { display: flex; align-items: flex-start; gap: 5px; margin: 3px 0; cursor: pointer; line-height: 1.3; }
.cbx .box { flex: 0 0 auto; display: inline-flex; align-items: center; justify-content: center; width: 12px; height: 12px; border: 1px solid #000; font-size: 10px; margin-top: 1px; }

/* 우측 서명 표 */
.s105-sigs { width: 100%; height: 100%; border-collapse: collapse; }
.s105-sigs td.sig-cell { border-bottom: 1px solid #000; padding: 6px 8px; vertical-align: top; }
.sig-hd { text-align: center; margin-bottom: 18px; }
.sig-line { text-align: center; color: #444; letter-spacing: -1px; overflow: hidden; }
.sig-name { text-align: center; font-weight: 600; }
.wit { font-size: 10px; line-height: 1.5; }
.wit-dots { color: #666; margin: 4px 0; }

/* 하단 */
.s105-note { padding: 6px 8px !important; font-size: 9.5px; line-height: 1.4; text-align: justify; }
.s105-line { padding: 4px 8px !important; }
.s105-foot { margin-top: 8px; font-size: 9.5px; line-height: 1.5; color: #333; }
</style>
